<template>
  <div class="sso-authorize-container">
    <div class="sso-authorize-card">
      <!-- Logo/Header -->
      <div class="sso-authorize-header">
        <div class="logo-container">
          <img src="@/assets/images/logo-dark.png" alt="Logo" class="sso-logo" />
        </div>
        <h4 class="sso-title">{{ authorizeTitle }}</h4>
        <p class="sso-message">{{ authorizeMessage }}</p>
      </div>

      <!-- User Info -->
      <div v-if="currentUser" class="user-info-card">
        <div class="user-avatar">
          <i class="ri-user-line"></i>
        </div>
        <div class="user-details">
          <h6 class="user-name">{{ currentUser.name }}</h6>
          <p class="user-email">{{ currentUser.email }}</p>
        </div>
      </div>

      <!-- Client Info -->
      <div v-if="clientInfo" class="client-info-card">
        <div class="client-header">
          <i class="ri-share-line client-icon"></i>
          <h6 class="client-title">Autorizando acceso a:</h6>
        </div>
        <div class="client-details">
          <div class="client-item">
            <span class="client-label">Cliente:</span>
            <span class="client-value">{{ clientInfo.client_name || clientInfo.client_id }}</span>
          </div>
          <div class="client-item">
            <span class="client-label">Dominio:</span>
            <span class="client-value">{{ getClientDomain() }}</span>
          </div>
        </div>
      </div>

      <!-- Progress Indicator -->
      <div class="authorize-progress">
        <div class="progress-step" :class="{ 'completed': steps.validation, 'active': currentStep === 'validation' }">
          <div class="step-icon">
            <i v-if="steps.validation" class="ri-check-line"></i>
            <div v-else-if="currentStep === 'validation'" class="spinner-border spinner-border-sm" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <i v-else class="ri-shield-check-line"></i>
          </div>
          <div class="step-info">
            <h6 class="step-title">Validando sesión</h6>
            <p class="step-description">Verificando credenciales...</p>
          </div>
        </div>

        <div class="progress-step" :class="{ 'completed': steps.authorization, 'active': currentStep === 'authorization' }">
          <div class="step-icon">
            <i v-if="steps.authorization" class="ri-check-line"></i>
            <div v-else-if="currentStep === 'authorization'" class="spinner-border spinner-border-sm" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <i v-else class="ri-key-line"></i>
          </div>
          <div class="step-info">
            <h6 class="step-title">Generando autorización</h6>
            <p class="step-description">Creando token de acceso...</p>
          </div>
        </div>

        <div class="progress-step" :class="{ 'completed': steps.redirect, 'active': currentStep === 'redirect' }">
          <div class="step-icon">
            <i v-if="steps.redirect" class="ri-check-line"></i>
            <div v-else-if="currentStep === 'redirect'" class="spinner-border spinner-border-sm" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <i v-else class="ri-external-link-line"></i>
          </div>
          <div class="step-info">
            <h6 class="step-title">Redirigiendo</h6>
            <p class="step-description">{{ redirectMessage }}</p>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="alert alert-danger mt-3">
        <i class="ri-error-warning-line mr-2"></i>
        {{ error }}
      </div>

      <!-- Manual Actions -->
      <div v-if="authorizationComplete && !autoRedirectEnabled" class="authorize-actions">
        <button 
          class="btn btn-primary"
          @click="performRedirect"
        >
          <i class="ri-external-link-line mr-1"></i>
          Ir a {{ getClientDomain() }}
        </button>
        <button 
          class="btn btn-link"
          @click="goToHome"
        >
          Cancelar y volver al inicio
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { authMethods, authComputed, notificationMethods } from "@/state/helpers";
import ssoService from "@/services/sso.service";

export default {
  name: "SSOAuthorize",
  
  data() {
    return {
      // Progress tracking
      currentStep: 'validation', // validation, authorization, redirect, complete
      steps: {
        validation: false,
        authorization: false,
        redirect: false
      },
      
      // Client & Authorization data
      clientInfo: null,
      authorizationData: null,
      redirectUrl: null,
      
      // UI state
      error: null,
      authorizationComplete: false,
      autoRedirectEnabled: true,
      
      // Messages
      authorizeTitle: "Autorizando acceso...",
      authorizeMessage: "Por favor espera mientras procesamos tu solicitud de forma segura.",
      redirectMessage: "Preparando redirección..."
    };
  },
  
  computed: {
    ...authComputed,
  },
  
  async created() {
    // Check if user is logged in
    if (!this.loggedIn) {
      // User not logged in, redirect to login with SSO params
      this.$router.push({
        name: 'login',
        query: this.$route.query
      });
      return;
    }

    await this.performAuthorization();
  },
  
  methods: {
    ...authMethods,
    ...notificationMethods,
    
    /**
     * Perform OAuth authorization flow
     */
    async performAuthorization() {
      try {
        // Step 1: Validation
        this.currentStep = 'validation';
        const ssoParams = ssoService.detectSSOParams();
        
        if (!ssoParams || !ssoParams.client_id || !ssoParams.redirect_uri) {
          throw new Error('Parámetros SSO inválidos o incompletos');
        }

        // Set client info for display
        this.clientInfo = {
          client_id: ssoParams.client_id,
          client_name: this.getClientName(ssoParams.client_id),
          redirect_uri: ssoParams.redirect_uri
        };

        await new Promise(resolve => setTimeout(resolve, 500));
        this.steps.validation = true;
        
        // Step 2: Authorization
        this.currentStep = 'authorization';
        this.authorizeMessage = `Generando acceso seguro para ${this.clientInfo.client_name}...`;
        
        const authData = await this.oauthAuthorize({
          clientId: ssoParams.client_id,
          redirectUri: ssoParams.redirect_uri,
          state: ssoParams.state,
          responseType: 'code',
          scope: 'read',
          refererUrl: ssoParams.referer_url
        });

        this.authorizationData = authData;
        this.steps.authorization = true;
        
        await new Promise(resolve => setTimeout(resolve, 500));
        // Step 3: Build redirect URL
        this.currentStep = 'redirect';
        this.redirectUrl = authData.redirect_url;
        this.redirectMessage = `Redirigiendo a ${this.getClientDomain()}...`;
        
        this.authorizeTitle = "¡Autorización exitosa!";
        this.authorizeMessage = "Acceso concedido correctamente.";
        
        await new Promise(resolve => setTimeout(resolve, 800));
        this.steps.redirect = true;
        
        // Perform redirect
        if (this.autoRedirectEnabled) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          this.performRedirect();
        } else {
          this.authorizationComplete = true;
        }
        
      } catch (error) {
        console.error('OAuth authorization error:', error);
        this.error = error.message || 'Error al autorizar la aplicación. Por favor intenta de nuevo.';
        this.authorizeTitle = "Error de autorización";
        this.authorizeMessage = "No se pudo completar el proceso de autorización.";
        this.autoRedirectEnabled = false;
        this.authorizationComplete = true;
      }
    },
    
    /**
     * Perform redirect to external application
     */
    performRedirect() {
      if (this.redirectUrl) {
        // Clean SSO params from URL
        ssoService.cleanSSOParamsFromURL();
        
        // Redirect
        window.location.href = this.redirectUrl;
      } else if (this.authorizationData?.redirect_uri) {
        window.location.href = this.authorizationData.redirect_uri;
      } else {
        this.error = 'No se encontró una URL de redirección válida';
      }
    },
    
    /**
     * Navigate to home page
     */
    goToHome() {
      // Clean SSO params
      ssoService.cleanSSOParamsFromURL();
      this.clearTempData();
      
      this.$router.push({ name: 'home' });
    },
    
    /**
     * Get client name from client_id
     */
    getClientName(clientId) {
      const clientNames = {
        'tienda_online': 'Tienda Online',
        'sitio_web': 'Sitio Web',
        'vue_frontend': 'Frontend Vue',
        'admin': 'Panel Administrativo'
      };
      
      return clientNames[clientId] || clientId;
    },
    
    /**
     * Get domain from redirect URI or client info
     */
    getClientDomain() {
      if (!this.clientInfo) return '';
      
      try {
        const url = new URL(this.clientInfo.redirect_uri);
        return url.hostname;
      } catch {
        return this.clientInfo.client_name || this.clientInfo.client_id;
      }
    }
  }
};
</script>

<!-- Styles are imported from src/assets/scss/custom/pages/_sso-authorize.scss -->

